/**
 * useAlertImage — an alert's picture, however this host is able to reach it.
 *
 * Two transports, one hook, because the alert list is drawn two ways (rows and cards) and
 * both need the same answer:
 *   • `thumbnailFor` — a directly-loadable URL (a node's own `image_urls`), used as-is.
 *   • `loadThumbnail` — a lazy resolver for hosts that can't load those URLs directly (the
 *     cloud streams each image through its tunnel as a blob). Awaited WITHOUT holding up the
 *     row, and the object URL it returns is revoked when the alert changes or unmounts.
 *
 * `src` is null in three different situations — no resolver, nothing resolved yet, and the
 * image 404'd — and the caller draws the same placeholder for all of them: down a list, a
 * frame that is missing for one reason should not look different from one missing for
 * another, and the operator can't act on the distinction anyway.
 *
 * A LAZY resolver additionally waits until its frame is on screen. `loading="lazy"` does that
 * for a direct URL and the browser gets it right; a blob streamed over a tunnel is our own
 * request, and twenty-five of them fired the moment a page renders is twenty-five round trips
 * through a node link for pictures the operator may scroll straight past. Attach the returned
 * `ref` to whatever box shows the image; a 250px margin starts the fetch just before the card
 * arrives, so scrolling still feels instant.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface AlertImage {
  /** The URL to draw, or null for the placeholder. */
  src: string | null;
  /** Hand to the <img>'s onError — a URL that exists but doesn't load is still no picture. */
  markFailed: () => void;
  /** Attach to the element showing the picture, so a lazy resolver waits for it to be seen. */
  ref: (node: Element | null) => void;
}

export function useAlertImage(
  alert: any,
  thumbnailFor?: (alert: any) => string | null,
  loadThumbnail?: (alert: any) => Promise<string | null>,
): AlertImage {
  const direct = thumbnailFor?.(alert) || null;
  const [resolved, setResolved] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  /** Has this frame been on screen? Latches: a card scrolled past and back doesn't refetch. */
  const [seen, setSeen] = useState(false);

  // Read through refs so the callback ref below can stay stable — a ref that changes identity
  // every render detaches and re-observes the node on every render with it.
  const lazyRef = useRef(false);
  lazyRef.current = !direct && !!loadThumbnail;
  const obsRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback((node: Element | null) => {
    obsRef.current?.disconnect();
    obsRef.current = null;
    // React passes null on unmount, which is what the disconnect above is for.
    if (!node || !lazyRef.current || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        setSeen(true);
        obs.disconnect();
      },
      { rootMargin: '250px' },
    );
    obs.observe(node);
    obsRef.current = obs;
  }, []);

  useEffect(() => () => obsRef.current?.disconnect(), []);

  // Keyed on the alert id, so paging the list re-resolves instead of showing the previous
  // page's picture under this page's text.
  useEffect(() => {
    setFailed(false);
    setResolved(null);
    if (direct || !loadThumbnail) return;
    // Not on screen yet. Where there is no IntersectionObserver at all there is nothing to
    // wait for, so the fetch goes ahead rather than never happening.
    if (!seen && typeof IntersectionObserver !== 'undefined') return;
    let cancelled = false;
    let made: string | null = null;
    loadThumbnail(alert)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          made = url;
          setResolved(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert?.id, direct, loadThumbnail, seen]);

  return { src: failed ? null : direct || resolved, markFailed: () => setFailed(true), ref };
}
